import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useState, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../../contexts/AuthContext';
import './Trial.css';
import { database } from '../../firebase';
import { ref, get } from 'firebase/database';
import { archiveCompanyApplicationTargets } from '../../utils/jobApplicationTargets';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL_PROD ||
  "http://localhost:8000" ||
  'http://localhost:8000';

const TrialStartButton = ({ onSuccess, className = '' }) => {
  useUiLanguage();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const resolveCompanyUid = useCallback(async () => {
    if (!currentUser?.uid) return null;

    let companyUid = currentUser.uid;
    try {
      const collabSnap = await get(ref(database, `collaborators/${currentUser.uid}`));
      if (collabSnap.exists() && collabSnap.val()?.companyId) {
        companyUid = collabSnap.val().companyId;
      }
    } catch (_) {
      // Ignore collaborator lookup failures; default to current user
    }
    return companyUid;
  }, [currentUser?.uid]);

  const notifyTrialWelcome = async (companyUid) => {
    if (!BACKEND_URL || !companyUid) return;

    try {
      const response = await fetch(`${BACKEND_URL}/send-trial-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ company_uid: companyUid }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to trigger trial welcome email:', errorText);
      }
    } catch (requestError) {
      console.error('Error triggering trial welcome email:', requestError);
    }
  };

  const handleStartTrial = async () => {
    setLoading(true);
    setError(null);

    try {
      const companyUid = await resolveCompanyUid();
      if (!companyUid) {
        throw new Error('Unable to resolve company account for trial activation.');
      }

      const functions = getFunctions();
      const startFreeTrial = httpsCallable(functions, 'startFreeTrial');

      const result = await startFreeTrial({
        companyInfo: {
          companyName: currentUser?.displayName || 'Your Company'
        }
      });

      if (result.data.success) {
        console.log('✅ Trial started:', result.data.message);
        await Promise.all([
          notifyTrialWelcome(companyUid),
          archiveCompanyApplicationTargets(database, companyUid)
        ]);

        if (onSuccess) {
          onSuccess(result.data.trial);
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Error starting trial:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="trial-start-container">
      <button
        onClick={handleStartTrial}
        disabled={loading}
        className={`trial-start-button ${className}`}
      >
        {loading ? (
          <>
            <span className="spinner"></span>{uiText("Starting Trial...")}</>
        ) : (
          <>
            <i className="fa-solid fa-rocket" style={{ marginRight: '8px' }}></i>{uiText("Start 14-Day Free Trial")}</>
        )}
      </button>

      <p className="trial-note">
        <i className="fa-solid fa-check-circle" style={{ color: 'var(--app-tone-51cf66, #51cf66)', marginRight: '6px' }}></i>{uiText("No credit card required • Full ATS + 10 TSE credits")}</p>

      {error && (
        <div className="trial-error">
          <p>
            <i className="fa-solid fa-triangle-exclamation" style={{ color: 'var(--app-tone-ff6b6b, #ff6b6b)', marginRight: '6px' }}></i>
            {uiText(error)}
          </p>
          {error.includes('already used') && (
            <p className="trial-error-hint">{uiText("You've already had a free trial.")}<a href="/employer-profile">{uiText("Subscribe now")}</a>{uiText(" to get access.")}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default TrialStartButton;
